/**
 * IncidentAck — parent-facing incident review + typed-name acknowledgement.
 *
 * Uses the same typed-name signature pattern shipped for Admissions
 * (audit-logged, IP recorded server-side).
 */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import BodyMap from "../components/BodyMap";

export default function IncidentAck() {
  const { t } = useI18n();
  const { id } = useParams();
  const nav = useNavigate();
  const [inc, setInc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typedName, setTypedName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/ops/me/incidents/${id}`);
        setInc(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("ops.errors.generic"));
      }
      setLoading(false);
    })();
  }, [id, t]);

  const sign = async () => {
    if (!typedName.trim()) {
      toast.error("Please type your full name to acknowledge.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/ops/me/incidents/${id}/ack`, { typed_name: typedName.trim() });
      toast.success("Acknowledged. Thank you for reviewing.");
      nav("/home");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ops.errors.generic"));
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <main className="max-w-2xl mx-auto p-6">
        <p className="font-serif italic text-[#525252]">{t("ops.common.loading")}</p>
      </main>
    );
  }
  if (!inc) return null;
  const already = Boolean(inc.parent_signature);

  return (
    <main className="max-w-2xl mx-auto px-5 pt-6 pb-24 space-y-6" data-testid="parent-incident-ack">
      <Link
        to="/home"
        className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#1A1B2E]"
      >
        <ArrowLeft size={14} strokeWidth={1.6} />
        {t("ops.parent_story.back_to_app")}
      </Link>

      <span className="eyebrow">{t("ops.incidents.eyebrow")}</span>
      <h1 className="font-display text-3xl text-[#1A1B2E]">{inc.incident_number}</h1>
      <p className="text-sm text-[#6B7280]">
        {inc.child_name} · {inc.occurred_at?.slice(0, 16).replace("T", " ")}
      </p>

      <section className="rounded-3xl bg-white border border-[#EEF0F4] p-5 space-y-3">
        <h3 className="font-display text-lg text-[#1A1B2E]">{t("ops.incidents.detail_summary")}</h3>
        <p className="text-sm text-[#1A1B2E] leading-relaxed">{inc.description}</p>
        <BodyMap selected={inc.body_map_locations || []} readOnly />
      </section>

      <section className="rounded-3xl bg-white border border-[#EEF0F4] p-5 space-y-3">
        <h3 className="font-display text-lg text-[#1A1B2E]">{t("ops.incidents.detail_action")}</h3>
        <p className="text-sm text-[#1A1B2E] leading-relaxed">{inc.action_taken}</p>
      </section>

      {already ? (
        <section
          className="rounded-3xl bg-[#FAF9F1] border border-[#EEF0F4] p-6 text-center"
          data-testid="incident-ack-already"
        >
          <ShieldCheck size={28} strokeWidth={1.4} className="text-[#1F3A2A] mx-auto" />
          <p className="mt-3 font-serif italic text-[#1A1B2E]">
            You acknowledged this report on{" "}
            {inc.parent_signature.signed_at?.slice(0, 16).replace("T", " ")}.
          </p>
          <p className="mt-2 text-sm text-[#6B7280]">
            "{inc.parent_signature.typed_name}"
          </p>
        </section>
      ) : (
        <section
          className="rounded-3xl bg-[#FAF9F1] border border-[#EEF0F4] p-6 space-y-4"
          data-testid="incident-ack-form"
        >
          <h3 className="font-display text-lg text-[#1A1B2E]">
            {t("ops.incidents.parent_ack_title")}
          </h3>
          <p className="text-sm text-[#6B7280] leading-relaxed">
            {t("ops.incidents.parent_ack_blurb")}
          </p>
          <input
            type="text"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder={t("ops.incidents.parent_ack_typed_name")}
            data-testid="incident-ack-typed-name"
            className="w-full px-3 py-2.5 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A] font-serif italic"
          />
          <button
            onClick={sign}
            disabled={submitting}
            data-testid="incident-ack-submit"
            className={`w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-5 py-2.5 hover:bg-[#132C17] ${submitting ? "opacity-60" : ""}`}
          >
            <ShieldCheck size={14} strokeWidth={1.8} />
            {t("ops.incidents.parent_ack_submit")}
          </button>
        </section>
      )}
    </main>
  );
}
